export default {
  data() {
    return {
      showGiveAllConfirm: false,
      giveAllCheckboxKey: 0,
      initialAccessState: null,
      preGiveAllAccessState: null,
      seededSubModules: [],
      openSubModuleTabs: {},
    };
  },
  computed: {
    availableModules() {
      return this.modulesData || this.modules || [];
    },
    allPrivilegeIds() {
      const ids = [];
      this.availableModules.forEach((module) => {
        (module.submodules || []).forEach((subModule) => {
          (subModule.privileges || []).forEach((privilege) => {
            if (privilege.id && !ids.includes(privilege.id)) {
              ids.push(privilege.id);
            }
          });
        });
      });
      return ids;
    },
    isGiveAllAccess() {
      return this.isFullAccessState(this.getCurrentAccessState());
    },
    addMultiple: {
      get() {
        if (!this.privileges.length) return false;
        return this.privileges.every((privilege) =>
          this.selectedPrivileges.includes(privilege.id)
        );
      },
      set(checked) {
        const privilegeIds = this.privileges.map((privilege) => privilege.id);

        if (checked) {
          this.updateSelectedPrivileges([
            ...this.selectedPrivileges,
            ...privilegeIds,
          ]);
          return;
        }

        this.updateSelectedPrivileges(
          this.selectedPrivileges.filter((id) => !privilegeIds.includes(id))
        );
      },
    },
    hasUnsavedChanges() {
      if (!this.initialAccessState) return false;
      return !this.accessStatesEqual(
        this.getCurrentAccessState(),
        this.initialAccessState
      );
    },
  },
  methods: {
    updateSelectedPrivileges(privilegeIds) {
      // Duplicate ids keep a privilege "selected" after it is unchecked,
      // which also keeps the sub-app and give-all checkboxes checked.
      this.selectedPrivileges = [...new Set(privilegeIds)];
    },
    subModuleKey(subModule) {
      return subModule.id || subModule.name;
    },
    handleTabChange(tabName, roleModule) {
      const subModules = (roleModule && roleModule.submodules) || [];
      const subModule = subModules.find((item) => item.name === tabName);

      this.currentTab = subModule ? subModule.name : "";
      this.privileges = subModule ? subModule.privileges || [] : [];
      this.subModuleName = subModule ? subModule.name : "";

      if (!subModule) return;
      // Remember the tab per app so reopening an app shows the same sub-app
      // instead of an empty panel.
      this.openSubModuleTabs = {
        ...this.openSubModuleTabs,
        [roleModule.id]: subModule.name,
      };
      this.seedSubModulePrivileges(subModule);
    },
    isAccordionOpen(openedIndex) {
      this.openedIndex = openedIndex;

      const roleModule =
        typeof openedIndex === "number"
          ? this.selectedModules[openedIndex]
          : null;

      if (!roleModule) {
        this.handleTabChange("", null);
        return;
      }

      const subModules = roleModule.submodules || [];
      const lastOpenTab = this.openSubModuleTabs[roleModule.id];
      const tabName =
        lastOpenTab || (subModules.length ? subModules[0].name : "");

      this.handleTabChange(tabName, roleModule);
    },
    seedSubModulePrivileges(subModule) {
      const key = this.subModuleKey(subModule);
      // Only seed once per sub-app, otherwise revisiting a tab re-selects
      // privileges the user just unchecked.
      if (this.seededSubModules.includes(key)) return;
      this.seededSubModules.push(key);

      const grantedIds = (subModule.privileges || [])
        .filter((privilege) => privilege.hasPrivilege)
        .map((privilege) => privilege.id);

      if (!grantedIds.length) return;
      this.updateSelectedPrivileges([
        ...this.selectedPrivileges,
        ...grantedIds,
      ]);
    },
    forgetSeededSubModules(module) {
      (module.submodules || []).forEach((subModule) => {
        const keyIndex = this.seededSubModules.indexOf(
          this.subModuleKey(subModule)
        );
        if (keyIndex > -1) this.seededSubModules.splice(keyIndex, 1);
      });
    },
    handleModules(modules) {
      this.openModal = false;
      modules.forEach((module) => {
        if (!this.selectedModules.find((item) => item.id === module.id)) {
          this.selectedModules.push(module);
          this.forgetSeededSubModules(module);
        }
      });
      this.isAccordionOpen(this.openedIndex);
    },
    deleteModule(id) {
      const moduleIndex = this.selectedModules.findIndex(
        (module) => module.id === id
      );
      if (moduleIndex === -1) return;

      const [removedModule] = this.selectedModules.splice(moduleIndex, 1);
      const removedPrivilegeIds = [];

      (removedModule.submodules || []).forEach((subModule) => {
        (subModule.privileges || []).forEach((privilege) => {
          removedPrivilegeIds.push(privilege.id);
        });
      });

      this.forgetSeededSubModules(removedModule);
      delete this.openSubModuleTabs[removedModule.id];
      this.updateSelectedPrivileges(
        this.selectedPrivileges.filter(
          (privilegeId) => !removedPrivilegeIds.includes(privilegeId)
        )
      );

      // Accordions are tracked by index, so keep the opened one in sync.
      if (moduleIndex === this.openedIndex) {
        this.isAccordionOpen(false);
      } else if (moduleIndex < this.openedIndex) {
        this.isAccordionOpen(this.openedIndex - 1);
      }
    },
    getCurrentAccessState() {
      return {
        selectedModules: this.selectedModules,
        selectedPrivileges: this.selectedPrivileges,
        role: {
          name: this.role.name || "",
          description: this.role.description || "",
        },
      };
    },
    snapshotAccessState() {
      const state = this.getCurrentAccessState();
      return {
        selectedModules: JSON.parse(JSON.stringify(state.selectedModules)),
        selectedPrivileges: [...new Set(state.selectedPrivileges)],
        role: { ...state.role },
      };
    },
    isFullAccessState(state) {
      if (!state) return false;
      const modules = this.availableModules;
      if (!modules.length || !this.allPrivilegeIds.length) return false;
      if (state.selectedModules.length !== modules.length) return false;
      return this.allPrivilegeIds.every((id) =>
        state.selectedPrivileges.includes(id)
      );
    },
    accessStatesEqual(firstState, secondState) {
      if (!firstState || !secondState) return false;

      const firstModuleIds = firstState.selectedModules
        .map((module) => module.id)
        .sort();
      const secondModuleIds = secondState.selectedModules
        .map((module) => module.id)
        .sort();
      const firstPrivilegeIds = [...firstState.selectedPrivileges].sort();
      const secondPrivilegeIds = [...secondState.selectedPrivileges].sort();

      return (
        firstState.role.name === secondState.role.name &&
        firstState.role.description === secondState.role.description &&
        firstModuleIds.length === secondModuleIds.length &&
        firstPrivilegeIds.length === secondPrivilegeIds.length &&
        firstModuleIds.every((id, index) => id === secondModuleIds[index]) &&
        firstPrivilegeIds.every((id, index) => id === secondPrivilegeIds[index])
      );
    },
    captureInitialAccessState() {
      const snapshot = this.snapshotAccessState();
      this.initialAccessState = snapshot;
      this.preGiveAllAccessState = snapshot;
    },
    clearWorkingAccessSelection() {
      this.selectedModules = [];
      this.selectedPrivileges = [];
      this.privileges = [];
      this.currentTab = "";
      this.subModuleName = "";
      this.seededSubModules = [];
      this.openSubModuleTabs = {};
    },
    restoreAccessState(state, { includeRole = true } = {}) {
      if (!state) return;

      this.selectedModules = JSON.parse(JSON.stringify(state.selectedModules));
      this.updateSelectedPrivileges(state.selectedPrivileges);
      // Re-point the open sub-app at the restored data instead of blanking it.
      this.isAccordionOpen(this.openedIndex);

      if (includeRole) {
        this.role.name = state.role.name;
        this.role.description = state.role.description;
      }
    },
    handleGiveAllAccessChange(checked) {
      if (checked) {
        this.preGiveAllAccessState = this.snapshotAccessState();
        this.showGiveAllConfirm = true;
        return;
      }

      this.undoGiveAllAccess();
    },
    cancelGiveAllAccess() {
      this.showGiveAllConfirm = false;
      // Remount checkbox so a cancelled click does not leave it visually checked
      this.giveAllCheckboxKey += 1;
    },
    undoGiveAllAccess() {
      const previousState = this.preGiveAllAccessState;
      const initialState = this.initialAccessState;

      // Restore whatever the user had before granting full access.
      if (previousState && !this.isFullAccessState(previousState)) {
        this.restoreAccessState(previousState, { includeRole: false });
        return;
      }

      // If the role already loaded with full access, clear the working
      // selection so the checkbox can uncheck. Reset restores the original.
      if (initialState && !this.isFullAccessState(initialState)) {
        this.restoreAccessState(initialState, { includeRole: false });
        return;
      }

      this.clearWorkingAccessSelection();
    },
    confirmGiveAllAccess() {
      const modules = this.availableModules;
      if (!modules.length || !this.allPrivilegeIds.length) {
        this.showGiveAllConfirm = false;
        return;
      }

      this.selectedModules = JSON.parse(JSON.stringify(modules));
      this.selectedPrivileges = [...this.allPrivilegeIds];
      this.showGiveAllConfirm = false;
    },
    resetAccessChanges() {
      this.restoreAccessState(this.initialAccessState);
      this.preGiveAllAccessState = this.snapshotAccessState();
    },
  },
};
